import { useState, useEffect, useCallback, useRef } from 'react';
import { useRemoteKeyboard } from '@/components/feature/RemoteKeyboardProvider';

export default function RemoteKeyboardOverlay() {
  const { isModalOpen, isConnected, qrCodeUrl, connectionToast, openQRModal, closeQRModal, closeSession } =
    useRemoteKeyboard();

  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const passThroughRef = useRef(false);
  const menuPosRef = useRef({ x: 0, y: 0 });
  const listeningRef = useRef(true);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (passThroughRef.current) {
        passThroughRef.current = false;
        return;
      }

      if (!listeningRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const menuWidth = 300;
      const menuHeight = 140;
      let x = e.clientX;
      let y = e.clientY;

      if (x + menuWidth > window.innerWidth) {
        x = window.innerWidth - menuWidth - 8;
      }
      if (y + menuHeight > window.innerHeight) {
        y = window.innerHeight - menuHeight - 8;
      }

      menuPosRef.current = { x: e.clientX, y: e.clientY };
      setMenuPos({ x, y });
      setMenuVisible(true);
    };

    document.addEventListener('contextmenu', handler, true);
    return () => {
      document.removeEventListener('contextmenu', handler, true);
    };
  }, []);

  const hideMenu = useCallback(() => {
    setMenuVisible(false);
  }, []);

  const handleShowNativeMenu = useCallback(() => {
    setMenuVisible(false);
    passThroughRef.current = true;
  }, []);

  const handleQrOption = useCallback(() => {
    setMenuVisible(false);
    openQRModal();
  }, [openQRModal]);

  useEffect(() => {
    if (!menuVisible) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        hideMenu();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        hideMenu();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuVisible, hideMenu]);

  const [copied, setCopied] = useState(false);
  const handleCopyUrl = useCallback(async () => {
    if (!qrCodeUrl) return;
    const basePath = (typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '') || '';
    const tokenMatch = qrCodeUrl.match(/token=([^&]+)/);
    const token = tokenMatch ? tokenMatch[1] : '';
    const url = `${window.location.origin}${basePath}/telecommande-clavier?token=${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [qrCodeUrl]);

  return (
    <>
      {menuVisible && (
        <div
          ref={menuRef}
          className="fixed z-[99999] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
          style={{ left: menuPos.x, top: menuPos.y, minWidth: 290 }}
        >
          <div className="h-1 w-full bg-gradient-to-r from-clinic-blue to-clinic-green"></div>

          <button
            onClick={handleQrOption}
            className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-clinic-green/8 whitespace-nowrap cursor-pointer"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-clinic-blue to-clinic-green text-white">
              <i className="ri-smartphone-line text-lg"></i>
            </span>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-gray-800">
                Utiliser le téléphone comme clavier
              </span>
              <span className="text-xs text-gray-500">
                Scanner le QR code avec votre téléphone
              </span>
            </div>
          </button>

          <div className="mx-3 border-t border-gray-100"></div>

          <button
            onClick={handleShowNativeMenu}
            className="flex w-full items-center gap-3 rounded-b-xl px-4 py-2.5 text-left text-xs text-gray-500 transition-colors hover:bg-gray-50 whitespace-nowrap cursor-pointer"
          >
            <span className="flex h-5 w-5 items-center justify-center text-gray-400">
              <i className="ri-mouse-line"></i>
            </span>
            Menu navigateur
          </button>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeQRModal}></div>
          <div className="relative z-10 mx-4 w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
            <div className="h-1.5 w-full bg-gradient-to-r from-clinic-blue to-clinic-green"></div>
            <div className="flex items-center justify-between px-6 pt-5 pb-3">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-clinic-blue to-clinic-green text-white">
                  <i className="ri-smartphone-line text-xl"></i>
                </span>
                <h2 className="text-lg font-bold text-gray-800">Clavier téléphone</h2>
              </div>
              <button
                onClick={closeQRModal}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="px-6 pb-6">
              {!isConnected ? (
                <>
                  <p className="mb-4 text-sm text-gray-600">
                    Scannez ce QR code avec l&apos;appareil photo de votre téléphone, puis tapez
                    directement depuis le clavier de votre téléphone.
                  </p>
                  <div className="mb-4 flex justify-center rounded-xl border-2 border-dashed border-clinic-blue/20 bg-gradient-to-br from-clinic-blue/3 to-clinic-green/3 p-5">
                    {qrCodeUrl ? (
                      <img
                        src={qrCodeUrl}
                        alt="QR Code clavier téléphone"
                        className="h-[240px] w-[240px] rounded-lg"
                        width={240}
                        height={240}
                      />
                    ) : (
                      <div className="flex h-[240px] w-[240px] items-center justify-center rounded-lg">
                        <i className="ri-loader-4-line animate-spin text-3xl text-gray-300"></i>
                      </div>
                    )}
                  </div>
                  <p className="mb-3 text-center text-xs text-gray-400">
                    En attente de connexion du téléphone...
                  </p>
                  <button
                    onClick={handleCopyUrl}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 whitespace-nowrap cursor-pointer"
                  >
                    <i
                      className={`${copied ? 'ri-check-line text-clinic-green' : 'ri-file-copy-line'}`}
                    ></i>
                    {copied ? 'Lien copié !' : 'Copier le lien'}
                  </button>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-clinic-green/10">
                    <i className="ri-check-line text-3xl text-clinic-green"></i>
                  </div>
                  <h3 className="mb-2 text-base font-semibold text-gray-800">
                    Téléphone connecté !
                  </h3>
                  <p className="mb-5 text-sm text-gray-500">
                    Tapez sur votre téléphone, le texte apparaîtra dans le champ sélectionné sur
                    l&apos;ordinateur.
                  </p>
                  <button
                    onClick={closeSession}
                    className="rounded-lg bg-red-50 px-5 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 whitespace-nowrap cursor-pointer"
                  >
                    Déconnecter
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {connectionToast && (
        <div className="pointer-events-none fixed bottom-6 left-1/2 z-[100001] -translate-x-1/2 animate-[slideUp_0.3s_ease-out]">
          <div className="flex items-center gap-2.5 rounded-xl bg-clinic-green px-5 py-3 text-sm font-semibold text-white shadow-lg">
            <i className="ri-checkbox-circle-line text-lg"></i>
            {connectionToast}
          </div>
        </div>
      )}
    </>
  );
}